import { fetchAuthSession } from 'aws-amplify/auth'
import { getEnvironmentConfig } from '../config/environment'
import type {
  ApiResponse,
  ApiError,
  RequestConfig,
  RetryConfig,
  SolicitudesResponse,
  SolicitudPayload,
  SolicitudResponse,
  SolicitudDetailResponse,
  CandidatesResponse,
  CandidatesParams,
  CandidateDetailResponse,
  UploadFileRequest,
  UploadUrlsResponse,
  // UploadCompletionRequest, // Commented out as it's not used
  PostulacionCreationResponse,
  UploadProgress,
  UploadResult,
  CVUrlResponse,
  StatusUpdateRequest,
  EmailRequest,
  EmailResponse,
  ValidationResult,
  PresignedUrlResponse,
  // Qualification Types
  QualificationUpdateRequest,
  QualificationUpdateResponse,
  BulkQualificationRequest,
  BulkQualificationResponse,
  // User Management Types
  UserInfo,
  CreateUserRequest,
  UpdateUserRequest,
  GetUsersResponse,
  RoleInfo,
  CreateRoleRequest,
  UpdateRoleRequest,
  GetRolesResponse,
  PaginationParams,
  PermissionResource,
  PermissionAction,
  // Workflow Types
  WorkflowConfiguration,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  GetWorkflowsResponse,
  WorkflowValidationResult,
  // Requirement Creation Types
  RequirementFieldConfig,
  RequirementFormData,
  RequirementFieldsResponse,
  CreateRequirementRequest,
  CreateRequirementResponse,
  // Job Definition Types
  JobDefinition,
  JobDefinitionFormData,
  CreateJobDefinitionRequest,
  UpdateJobDefinitionRequest,
  JobDefinitionListResponse,
  JobDefinitionResponse,
  JobDefinitionSelectorOption,
  // Google Forms Integration Types
  IntegrationConfiguration,
  CreateIntegrationConfigRequest,
  UpdateIntegrationConfigRequest,
  IntegrationConfigResponse,
  JobIntegration,
  CreateJobIntegrationRequest,
  UpdateJobIntegrationRequest,
  JobIntegrationResponse,
} from '../types'
import { UserManagementError } from '../types'

/**
 * Centralized API service for handling all HTTP requests
 * Includes authentication, error handling, retry logic, and response transformation
 */
export class ApiService {
  private baseURL: string
  private postulacionesURL: string
  private postulantesURL: string
  private closeURL: string
  private statusURL: string
  private usersURL: string
  private rolesURL: string
  private workflowsURL: string
  private jobDefinitionsURL: string
  private integrationsURL: string
  private defaultTimeout: number = 30000
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    retryDelay: 1000,
    retryCondition: (error: ApiError) => {
      // Retry on network errors and 5xx server errors
      return error.status >= 500 || error.status === 0
    },
  }

  constructor() {
    // Initialize API endpoints from environment variables
    const config = getEnvironmentConfig()
    this.baseURL = `${config.API_URL}/solicitudes`
    this.postulacionesURL = config.API_POSTULACIONES_URL
    this.postulantesURL = config.API_POSTULANTES_URL
    this.closeURL = config.API_CLOSE_URL
    this.statusURL = config.API_STATUS_URL

    // User management endpoints
    this.usersURL = `${config.API_URL}/users`
    this.rolesURL = `${config.API_URL}/roles`

    // Workflow management endpoints
    this.workflowsURL = `${config.API_URL}/workflows`

    // Job definition endpoints
    this.jobDefinitionsURL = `${config.API_URL}/job-definitions`

    // Integration endpoints
    this.integrationsURL = `${config.API_URL}/integrations`

    if (!config.API_URL) {
      console.warn('VITE_API_URL not configured')
    }
  }

  /**
   * Get authentication token from AWS Cognito
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession()
      // Use access token for API authentication (not ID token)
      return session.tokens?.accessToken?.toString() || null
    } catch (error) {
      console.warn('Failed to get auth token:', error)
      return null
    }
  }

  /**
   * Create HTTP request with authentication and error handling
   */
  private async makeRequest<T>(config: RequestConfig): Promise<T> {
    const {
      method,
      url,
      data,
      params,
      headers = {},
      timeout = this.defaultTimeout,
    } = config

    // Add authentication token
    const token = await this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Add default headers
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
    headers['Accept'] = 'application/json'

    // Build URL with query parameters
    const urlObj = new URL(url)
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          urlObj.searchParams.append(key, String(value))
        }
      })
    }

    // Create AbortController for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(urlObj.toString(), {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw await this.createApiError(response)
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T
      }

      const result = await response.json()
      return result as T
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout')
      }

      throw error
    }
  }

  /**
   * Create standardized API error from response
   */
  private async createApiError(response: Response): Promise<Error> {
    let message = `HTTP ${response.status}: ${response.statusText}`
    let details: unknown = null

    try {
      const errorData = await response.json()

      // Try to extract error message from various possible structures
      if (typeof errorData.message === 'string') {
        message = errorData.message
      } else if (typeof errorData.error === 'string') {
        message = errorData.error
      } else if (
        errorData.error &&
        typeof errorData.error.message === 'string'
      ) {
        // Handle nested error structure like: { error: { code: "...", message: "..." } }
        message = errorData.error.message
      } else if (errorData.error && typeof errorData.error.error === 'string') {
        message = errorData.error.error
      } else if (
        errorData.details &&
        typeof errorData.details.message === 'string'
      ) {
        message = errorData.details.message
      } else if (errorData.data && typeof errorData.data.message === 'string') {
        message = errorData.data.message
      }

      details = errorData
    } catch {
      // Use default message if response is not JSON
    }

    // Create Error instance with ApiError properties
    const error = new Error(message) as Error & ApiError
    error.status = response.status
    error.details = details

    return error
  }

  /**
   * Execute request with retry logic
   */
  private async executeWithRetry<T>(config: RequestConfig): Promise<T> {
    let lastError: ApiError | Error

    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        return await this.makeRequest<T>(config)
      } catch (error) {
        lastError = error as ApiError | Error

        // Don't retry on last attempt
        if (attempt === this.retryConfig.maxRetries) {
          break
        }

        // Check if error should be retried
        const apiError = error as ApiError
        if (apiError.status && !this.retryConfig.retryCondition?.(apiError)) {
          break
        }

        // Wait before retry with exponential backoff
        const delay = this.retryConfig.retryDelay * Math.pow(2, attempt)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    throw lastError!
  }

  // Solicitudes (Job Postings) API Methods

  /**
   * Get list of solicitudes with pagination
   */
  async getSolicitudes(
    size?: number,
    startKey?: string
  ): Promise<SolicitudesResponse> {
    return this.executeWithRetry<SolicitudesResponse>({
      method: 'GET',
      url: this.baseURL,
      params: {
        ...(size && { size }),
        ...(startKey && { start: startKey }),
        estado: 'active', // Only active solicitudes in simplified workflow
      },
    })
  }

  /**
   * Create new solicitud
   * Supports both traditional job definition creation and predefined job definition selection
   */
  async createSolicitud(payload: SolicitudPayload): Promise<SolicitudResponse> {
    // Validate job definition ID if provided
    if (payload.job_definition_id && !payload.job_definition_id.trim()) {
      throw new Error('Job definition ID cannot be empty')
    }

    const response = await this.executeWithRetry<
      ApiResponse<SolicitudResponse>
    >({
      method: 'POST',
      url: this.baseURL,
      data: payload,
    })
    return response.data
  }

  /**
   * Get specific solicitud by ID
   */
  async getSolicitud(id: string): Promise<SolicitudDetailResponse> {
    return this.executeWithRetry<SolicitudDetailResponse>({
      method: 'GET',
      url: `${this.baseURL}/${id}`,
    })
  }

  // Candidates (Postulaciones) API Methods

  /**
   * Get candidates for a specific solicitud
   * Supports filtering by qualification_status, processing_status, and other parameters
   */
  async getCandidates(params: CandidatesParams): Promise<CandidatesResponse> {
    const { id_solicitud, ...queryParams } = params
    return this.executeWithRetry<CandidatesResponse>({
      method: 'GET',
      url: `${this.postulacionesURL}/${id_solicitud}`,
      params: queryParams,
    })
  }

  /**
   * Get specific candidate details
   * Includes qualification metadata (status, qualified_by, qualified_at)
   */
  async getCandidateDetail(id: string): Promise<CandidateDetailResponse> {
    return this.executeWithRetry<CandidateDetailResponse>({
      method: 'GET',
      url: `${this.postulantesURL}/${id}`,
    })
  }

  /**
   * Update candidate status (bulk update)
   */
  async updateCandidateStatus(updates: StatusUpdateRequest): Promise<void> {
    await this.executeWithRetry<void>({
      method: 'PATCH',
      url: this.statusURL,
      data: updates,
    })
  }

  /**
   * Update candidate qualification status
   */
  async updateCandidateQualification(
    candidateId: string,
    qualificationData: QualificationUpdateRequest
  ): Promise<QualificationUpdateResponse> {
    try {
      if (!candidateId) {
        throw new Error('Candidate ID is required')
      }

      // Validate qualification status
      const validStatuses = ['califica', 'no_califica']
      if (!validStatuses.includes(qualificationData.qualification_status)) {
        throw new Error('Invalid qualification status')
      }

      const config = getEnvironmentConfig()
      const response = await this.executeWithRetry<
        ApiResponse<QualificationUpdateResponse>
      >({
        method: 'POST',
        url: `${config.API_URL}/candidates/${candidateId}/qualification`,
        data: qualificationData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to update candidate qualification'
      )
    }
  }

  /**
   * Bulk update candidate qualification status
   */
  async bulkUpdateCandidateQualification(
    bulkData: BulkQualificationRequest
  ): Promise<BulkQualificationResponse> {
    try {
      if (!bulkData.candidate_ids || bulkData.candidate_ids.length === 0) {
        throw new Error('At least one candidate ID is required')
      }

      // Validate qualification status
      const validStatuses = ['califica', 'no_califica']
      if (!validStatuses.includes(bulkData.qualification_status)) {
        throw new Error('Invalid qualification status')
      }

      const config = getEnvironmentConfig()
      const response = await this.executeWithRetry<
        ApiResponse<BulkQualificationResponse>
      >({
        method: 'POST',
        url: `${config.API_URL}/candidates/bulk-qualification`,
        data: bulkData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to bulk update candidate qualifications'
      )
    }
  }

  // File Upload API Methods

  /**
   * Request presigned URLs for file uploads
   */
  async requestUploadUrls(
    solicitudId: string,
    files: UploadFileRequest[]
  ): Promise<UploadUrlsResponse> {
    const response = await this.executeWithRetry<
      ApiResponse<UploadUrlsResponse>
    >({
      method: 'POST',
      url: `${this.postulacionesURL}/${solicitudId}/upload-urls`,
      data: { files },
    })
    return response.data
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  async uploadFileToS3(
    file: File,
    presignedUrl: PresignedUrlResponse
  ): Promise<UploadProgress> {
    return new Promise(resolve => {
      const xhr = new XMLHttpRequest()
      let resolved = false

      // Track upload progress (but don't resolve the promise yet)
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          // For now, we'll just log progress. In a full implementation,
          // you might want to use a callback or event system for progress updates
          console.log(`Upload progress for ${file.name}: ${progress}%`)
        }
      })

      // Handle completion
      xhr.addEventListener('load', () => {
        if (resolved) return
        resolved = true

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({
            filename: file.name,
            progress: 100,
            status: 'completed',
          })
        } else {
          resolve({
            filename: file.name,
            progress: 0,
            status: 'error',
            error: `Upload failed with status ${xhr.status}`,
          })
        }
      })

      // Handle errors
      xhr.addEventListener('error', () => {
        if (resolved) return
        resolved = true

        resolve({
          filename: file.name,
          progress: 0,
          status: 'error',
          error: 'Network error during upload',
        })
      })

      // Handle timeout
      xhr.addEventListener('timeout', () => {
        if (resolved) return
        resolved = true

        resolve({
          filename: file.name,
          progress: 0,
          status: 'error',
          error: 'Upload timeout',
        })
      })

      // Configure and send request
      xhr.open('PUT', presignedUrl.upload_url)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.timeout = 300000 // 5 minutes timeout
      xhr.send(file)
    })
  }

  /**
   * Create postulacion records after successful uploads
   */
  async createPostulacionRecords(
    solicitudId: string,
    uploads: Array<{ s3_key: string; filename: string }>
  ): Promise<PostulacionCreationResponse> {
    const url = `${this.postulacionesURL}/${solicitudId}/cvs`
    console.log('createPostulacionRecords:', { url, solicitudId, uploads })

    const response = await this.executeWithRetry<
      ApiResponse<PostulacionCreationResponse>
    >({
      method: 'POST',
      url,
      data: { uploads },
    })

    console.log('createPostulacionRecords response:', response)
    return response.data
  }

  /**
   * Complete CV upload workflow: request URLs, upload files, create records
   */
  async uploadCVFiles(
    solicitudId: string,
    files: File[]
  ): Promise<UploadResult> {
    console.log('uploadCVFiles started:', {
      solicitudId,
      fileCount: files.length,
    })

    // Validate files
    const validation = this.validateUploadFiles(files)
    if (!validation.isValid) {
      console.error('File validation failed:', validation.errors)
      throw new Error(validation.errors.join(', '))
    }

    // Prepare file requests
    const fileRequests: UploadFileRequest[] = files.map(file => ({
      filename: file.name,
      content_type: file.type,
      size: file.size,
    }))

    // Request presigned URLs
    const urlsResponse = await this.requestUploadUrls(solicitudId, fileRequests)

    // Initialize progress tracking
    const uploadProgress: UploadProgress[] = files.map(file => ({
      filename: file.name,
      progress: 0,
      status: 'pending',
    }))

    // Upload files in parallel
    const uploadPromises = files.map(async (file, index) => {
      const presignedUrl = urlsResponse.upload_urls[index]
      const progress = await this.uploadFileToS3(file, presignedUrl)

      // Update progress
      uploadProgress[index] = progress

      if (progress.status === 'completed') {
        return {
          s3_key: presignedUrl.s3_key,
          filename: file.name,
        }
      } else {
        throw new Error(progress.error || 'Upload failed')
      }
    })

    try {
      const completedUploads = await Promise.all(uploadPromises)
      console.log('Files uploaded successfully:', completedUploads)

      // Create postulacion records
      console.log('Creating postulacion records...')
      const creationResponse = await this.createPostulacionRecords(
        solicitudId,
        completedUploads
      )
      console.log('Postulacion records created:', creationResponse)

      return {
        progress: uploadProgress,
        completed: creationResponse,
      }
    } catch (error) {
      console.error('Error in uploadCVFiles:', error)
      throw error
    }
  }

  // CV Viewing API Methods

  /**
   * Get secure CV URL for viewing
   */
  async getCVUrl(candidateId: string): Promise<CVUrlResponse> {
    return this.executeWithRetry<CVUrlResponse>({
      method: 'GET',
      url: `${this.postulantesURL}/${candidateId}/cv-url`,
    })
  }

  // Email Notification API Methods

  /**
   * Send thank you emails and close solicitud
   */
  async sendThankYouEmails(
    solicitudId: string,
    request: EmailRequest
  ): Promise<EmailResponse> {
    const response = await this.executeWithRetry<ApiResponse<EmailResponse>>({
      method: 'POST',
      url: `${this.closeURL}/${solicitudId}`,
      data: request,
    })
    return response.data
  }

  // User Management API Methods

  /**
   * Get list of users with pagination and filtering
   */
  async getUsers(params?: PaginationParams): Promise<GetUsersResponse> {
    try {
      return await this.executeWithRetry<GetUsersResponse>({
        method: 'GET',
        url: this.usersURL,
        params: params
          ? {
              ...(params.size && { size: params.size }),
              ...(params.start && { start: params.start }),
            }
          : undefined,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to fetch users')
    }
  }

  /**
   * Create new user
   */
  async createUser(userData: CreateUserRequest): Promise<UserInfo> {
    try {
      // Validate user data before sending
      const validation = this.validateUserData(userData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await this.executeWithRetry<ApiResponse<UserInfo>>({
        method: 'POST',
        url: this.usersURL,
        data: userData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to create user')
    }
  }

  /**
   * Update existing user
   */
  async updateUser(
    userId: string,
    userData: UpdateUserRequest
  ): Promise<UserInfo> {
    try {
      if (!userId) {
        throw new Error('User ID is required')
      }

      // Validate update data
      const validation = this.validateUserUpdateData(userData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await this.executeWithRetry<ApiResponse<UserInfo>>({
        method: 'PUT',
        url: `${this.usersURL}/${userId}`,
        data: userData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to update user')
    }
  }

  /**
   * Delete user
   */
  async deleteUser(
    userId: string,
    deletionType: 'soft' | 'hard' = 'hard'
  ): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID is required')
      }

      console.log('API Service - Deleting user with ID:', userId)
      console.log(
        'Delete URL:',
        `${this.usersURL}/${userId}?type=${deletionType}`
      )

      await this.executeWithRetry<void>({
        method: 'DELETE',
        url: `${this.usersURL}/${userId}?type=${deletionType}`,
      })
    } catch (error) {
      console.error('API Service - Delete user error:', error)
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to delete user')
    }
  }

  // Role Management API Methods

  /**
   * Get list of all roles
   */
  async getRoles(): Promise<GetRolesResponse> {
    try {
      return await this.executeWithRetry<GetRolesResponse>({
        method: 'GET',
        url: this.rolesURL,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to fetch roles')
    }
  }

  /**
   * Create new role
   */
  async createRole(roleData: CreateRoleRequest): Promise<RoleInfo> {
    try {
      // Validate role data before sending
      const validation = this.validateRoleData(roleData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await this.executeWithRetry<ApiResponse<RoleInfo>>({
        method: 'POST',
        url: this.rolesURL,
        data: roleData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to create role')
    }
  }

  /**
   * Update existing role
   */
  async updateRole(
    roleId: string,
    roleData: UpdateRoleRequest
  ): Promise<RoleInfo> {
    try {
      if (!roleId) {
        throw new Error('Role ID is required')
      }

      // Validate update data
      const validation = this.validateRoleUpdateData(roleData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await this.executeWithRetry<ApiResponse<RoleInfo>>({
        method: 'PUT',
        url: `${this.rolesURL}/${roleId}`,
        data: roleData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to update role')
    }
  }

  /**
   * Delete role
   */
  async deleteRole(roleId: string): Promise<void> {
    try {
      if (!roleId) {
        throw new Error('Role ID is required')
      }

      await this.executeWithRetry<void>({
        method: 'DELETE',
        url: `${this.rolesURL}/${roleId}`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(apiError, 'Failed to delete role')
    }
  }

  // Workflow Management API Methods

  /**
   * Get list of all workflows
   */
  async getWorkflows(): Promise<GetWorkflowsResponse> {
    try {
      return await this.executeWithRetry<GetWorkflowsResponse>({
        method: 'GET',
        url: this.workflowsURL,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch workflows'
      )
    }
  }

  /**
   * Get active workflow
   */
  async getActiveWorkflow(): Promise<WorkflowConfiguration | null> {
    try {
      const response = await this.executeWithRetry<
        ApiResponse<{
          active_workflow: WorkflowConfiguration | null
          auto_approval: boolean
          message: string
        }>
      >({
        method: 'GET',
        url: `${this.workflowsURL}/active`,
      })
      return response.data.active_workflow
    } catch (error) {
      const apiError = error as ApiError
      if (apiError.status === 404) {
        return null // No active workflow
      }
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch active workflow'
      )
    }
  }

  /**
   * Create new workflow
   */
  async createWorkflow(
    workflowData: CreateWorkflowRequest
  ): Promise<WorkflowConfiguration> {
    try {
      // Validate workflow data before sending
      const validation = this.validateWorkflowData(workflowData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await this.executeWithRetry<
        ApiResponse<WorkflowConfiguration>
      >({
        method: 'POST',
        url: this.workflowsURL,
        data: workflowData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to create workflow'
      )
    }
  }

  /**
   * Update existing workflow
   */
  async updateWorkflow(
    workflowId: string,
    workflowData: UpdateWorkflowRequest
  ): Promise<WorkflowConfiguration> {
    try {
      if (!workflowId) {
        throw new Error('Workflow ID is required')
      }

      // Validate update data
      const validation = this.validateWorkflowUpdateData(workflowData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await this.executeWithRetry<
        ApiResponse<WorkflowConfiguration>
      >({
        method: 'PUT',
        url: `${this.workflowsURL}/${workflowId}`,
        data: workflowData,
      })
      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to update workflow'
      )
    }
  }

  /**
   * Delete workflow
   */
  async deleteWorkflow(workflowId: string): Promise<void> {
    try {
      if (!workflowId) {
        throw new Error('Workflow ID is required')
      }

      await this.executeWithRetry<void>({
        method: 'DELETE',
        url: `${this.workflowsURL}/${workflowId}`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to delete workflow'
      )
    }
  }

  /**
   * Get workflow status for a solicitud
   */
  async getWorkflowStatus(solicitudId: string, userId?: string): Promise<any> {
    try {
      const params: Record<string, string> = {}
      if (userId) {
        params.user_id = userId
      }

      const response = await this.executeWithRetry<any>({
        method: 'GET',
        url: `${this.baseURL}/${solicitudId}/workflow-status`,
        params: Object.keys(params).length > 0 ? params : undefined,
      })
      return response
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch workflow status'
      )
    }
  }

  /**
   * Check if CV uploads are allowed for a solicitud
   */
  async checkUploadPermission(solicitudId: string): Promise<{
    upload_allowed: boolean
    reason?: string
    approval_status: 'pending' | 'approved' | 'rejected'
    workflow_info?: any
  }> {
    try {
      const response = await this.executeWithRetry<
        ApiResponse<{
          upload_allowed: boolean
          reason?: string
          approval_status: 'pending' | 'approved' | 'rejected'
          workflow_info?: any
        }>
      >({
        method: 'GET',
        url: `${this.baseURL}/${solicitudId}/upload-permission`,
      })

      return response.data
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to check upload permission'
      )
    }
  }

  /**
   * Submit approval for a solicitud
   */
  async submitApproval(
    solicitudId: string,
    action: 'approve' | 'reject',
    comments?: string
  ): Promise<void> {
    try {
      const endpoint = action === 'approve' ? 'approve' : 'reject'
      const data: any = {}
      if (comments) {
        data.comments = comments
      }

      await this.executeWithRetry<void>({
        method: 'POST',
        url: `${this.baseURL}/${solicitudId}/${endpoint}`,
        data: Object.keys(data).length > 0 ? data : undefined,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        `Failed to ${action} solicitud`
      )
    }
  }

  /**
   * Get pending approvals for a user
   */
  async getPendingApprovals(userId: string): Promise<any> {
    try {
      const response = await this.executeWithRetry<any>({
        method: 'GET',
        url: `${this.baseURL}/pending-approvals`,
        params: { user_id: userId },
      })
      return response
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch pending approvals'
      )
    }
  }

  /**
   * Approve a solicitud
   */
  async approveSolicitud(solicitudId: string, comments?: string): Promise<any> {
    try {
      const response = await this.executeWithRetry<any>({
        method: 'POST',
        url: `${this.baseURL}/${solicitudId}/approve`,
        data: { comments: comments || '' },
      })
      return response
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to approve solicitud'
      )
    }
  }

  /**
   * Reject a solicitud
   */
  async rejectSolicitud(solicitudId: string, comments: string): Promise<any> {
    try {
      const response = await this.executeWithRetry<any>({
        method: 'POST',
        url: `${this.baseURL}/${solicitudId}/reject`,
        data: { comments },
      })
      return response
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to reject solicitud'
      )
    }
  }

  // Requirement Creation System API Methods

  /**
   * Get requirement field configurations
   */
  async getRequirementFields(): Promise<RequirementFieldsResponse> {
    try {
      const config = getEnvironmentConfig()
      return await this.executeWithRetry<RequirementFieldsResponse>({
        method: 'GET',
        url: `${config.API_URL}/requirement-fields`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch requirement fields'
      )
    }
  }

  /**
   * Create new requirement
   */
  async createRequirement(
    requirementData: CreateRequirementRequest
  ): Promise<CreateRequirementResponse> {
    try {
      const config = getEnvironmentConfig()
      const response = await this.executeWithRetry<CreateRequirementResponse>({
        method: 'POST',
        url: `${config.API_URL}/requirements`,
        data: requirementData,
      })
      return response
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to create requirement'
      )
    }
  }

  /**
   * Get requirement details
   */
  async getRequirement(requirementId: string): Promise<any> {
    try {
      const config = getEnvironmentConfig()
      return await this.executeWithRetry<any>({
        method: 'GET',
        url: `${config.API_URL}/requirements/${requirementId}`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch requirement'
      )
    }
  }

  /**
   * Get requirement status
   */
  async getRequirementStatus(requirementId: string): Promise<any> {
    try {
      const config = getEnvironmentConfig()
      return await this.executeWithRetry<any>({
        method: 'GET',
        url: `${config.API_URL}/requirements/${requirementId}/status`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch requirement status'
      )
    }
  }

  /**
   * Update job definition for approved requirement
   */
  async updateSolicitudJobDefinition(
    solicitudId: string,
    jobDefinitionData: SolicitudPayload
  ): Promise<any> {
    try {
      const config = getEnvironmentConfig()
      return await this.executeWithRetry<any>({
        method: 'PUT',
        url: `${config.API_URL}/solicitudes/${solicitudId}/job-definition`,
        data: jobDefinitionData,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to update job definition'
      )
    }
  }

  // ===== GOOGLE FORMS INTEGRATION MANAGEMENT =====

  /**
   * Get Google Forms integration configuration
   */
  async getIntegrationConfig(): Promise<IntegrationConfigResponse> {
    try {
      return await this.executeWithRetry<IntegrationConfigResponse>({
        method: 'GET',
        url: `${this.integrationsURL}/google-forms`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch integration configuration'
      )
    }
  }

  /**
   * Create or update Google Forms integration configuration
   */
  async createIntegrationConfig(
    configData: CreateIntegrationConfigRequest
  ): Promise<IntegrationConfigResponse> {
    try {
      // Validate service account credentials JSON
      try {
        JSON.parse(configData.service_account_credentials)
      } catch {
        throw new Error('Invalid service account credentials JSON format')
      }

      const response = await this.executeWithRetry<
        ApiResponse<IntegrationConfiguration>
      >({
        method: 'POST',
        url: `${this.integrationsURL}/google-forms`,
        data: configData,
      })
      return { data: response.data }
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to create integration configuration'
      )
    }
  }

  /**
   * Update Google Forms integration configuration
   */
  async updateIntegrationConfig(
    configData: UpdateIntegrationConfigRequest
  ): Promise<IntegrationConfigResponse> {
    try {
      // Validate service account credentials JSON if provided
      if (configData.service_account_credentials) {
        try {
          JSON.parse(configData.service_account_credentials)
        } catch {
          throw new Error('Invalid service account credentials JSON format')
        }
      }

      const response = await this.executeWithRetry<
        ApiResponse<IntegrationConfiguration>
      >({
        method: 'PUT',
        url: `${this.integrationsURL}/google-forms`,
        data: configData,
      })
      return { data: response.data }
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to update integration configuration'
      )
    }
  }

  /**
   * Get job integration configuration for a specific solicitud
   */
  async getJobIntegration(
    solicitudId: string
  ): Promise<JobIntegrationResponse> {
    try {
      if (!solicitudId) {
        throw new Error('Solicitud ID is required')
      }

      return await this.executeWithRetry<JobIntegrationResponse>({
        method: 'GET',
        url: `${this.baseURL}/${solicitudId}/integrations`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch job integration'
      )
    }
  }

  /**
   * Create job integration configuration for a specific solicitud
   */
  async createJobIntegration(
    solicitudId: string,
    integrationData: CreateJobIntegrationRequest
  ): Promise<JobIntegrationResponse> {
    try {
      if (!solicitudId) {
        throw new Error('Solicitud ID is required')
      }

      // Validate Google Sheets URL format
      const validation = this.validateGoogleSheetsUrl(
        integrationData.spreadsheet_url
      )
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await this.executeWithRetry<ApiResponse<JobIntegration>>(
        {
          method: 'POST',
          url: `${this.baseURL}/${solicitudId}/integrations`,
          data: integrationData,
        }
      )
      return { data: response.data }
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to create job integration'
      )
    }
  }

  /**
   * Update job integration configuration for a specific solicitud
   */
  async updateJobIntegration(
    solicitudId: string,
    integrationData: UpdateJobIntegrationRequest
  ): Promise<JobIntegrationResponse> {
    try {
      if (!solicitudId) {
        throw new Error('Solicitud ID is required')
      }

      // Validate Google Sheets URL format if provided
      if (integrationData.spreadsheet_url) {
        const validation = this.validateGoogleSheetsUrl(
          integrationData.spreadsheet_url
        )
        if (!validation.isValid) {
          throw new Error(validation.errors.join(', '))
        }
      }

      const response = await this.executeWithRetry<ApiResponse<JobIntegration>>(
        {
          method: 'PUT',
          url: `${this.baseURL}/${solicitudId}/integrations`,
          data: integrationData,
        }
      )
      return { data: response.data }
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to update job integration'
      )
    }
  }

  /**
   * Delete job integration configuration for a specific solicitud
   */
  async deleteJobIntegration(solicitudId: string): Promise<void> {
    try {
      if (!solicitudId) {
        throw new Error('Solicitud ID is required')
      }

      await this.executeWithRetry<void>({
        method: 'DELETE',
        url: `${this.baseURL}/${solicitudId}/integrations`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to delete job integration'
      )
    }
  }

  /**
   * Validate Google Sheets URL format
   */
  validateGoogleSheetsUrl(url: string): ValidationResult {
    const errors: string[] = []

    if (!url || !url.trim()) {
      errors.push('Google Sheets URL is required')
      return { isValid: false, errors }
    }

    // Check if it's a valid Google Sheets URL
    const googleSheetsPattern =
      /^https:\/\/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/
    if (!googleSheetsPattern.test(url)) {
      errors.push(
        'Invalid Google Sheets URL format. URL should start with https://docs.google.com/spreadsheets/d/'
      )
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  // Validation Methods

  /**
   * Validate solicitud payload before submission
   */
  validateSolicitudPayload(payload: SolicitudPayload): ValidationResult {
    const errors: string[] = []

    // Validate required fields
    if (!payload.estructura?.puesto_solicitado?.puesto?.trim()) {
      errors.push('Nombre de la posición es obligatorio')
    }

    if (!payload.funciones?.length) {
      errors.push('Al menos una función es obligatoria')
    }

    // Validate ocupabilidad totals to 100%
    if (payload.funciones?.length) {
      const totalOcupabilidad = payload.funciones.reduce((total, funcion) => {
        return total + (funcion.ocupabilidad || 0)
      }, 0)

      if (totalOcupabilidad !== 100) {
        errors.push(
          `La ocupabilidad total debe ser 100%, actualmente es ${totalOcupabilidad}%`
        )
      }
    }

    // Validate individual function ocupabilidad
    payload.funciones?.forEach((funcion, index) => {
      if (!funcion.funcion?.trim()) {
        errors.push(`La función ${index + 1} no puede estar vacía`)
      }
      if (funcion.ocupabilidad < 1 || funcion.ocupabilidad > 100) {
        errors.push(
          `La ocupabilidad de la función ${index + 1} debe estar entre 1% y 100%`
        )
      }
    })

    // Validate requisitos (only if they exist and are not empty)
    payload.requisitos?.indispensables?.forEach((requisito, index) => {
      if (requisito && !requisito.trim()) {
        errors.push(`El requisito ${index + 1} no puede estar vacío`)
      }
    })

    // Validate perfil estudios if present
    payload.perfil?.estudios?.forEach((estudio, index) => {
      const hasNivel = estudio.nivel && estudio.nivel.trim()
      const hasDetalle = estudio.detalle && estudio.detalle.trim()

      if ((hasNivel || hasDetalle) && (!hasNivel || !hasDetalle)) {
        errors.push(
          `El estudio ${index + 1} debe tener nivel y detalle completos`
        )
      }
    })

    // Validate perfil idiomas if present
    payload.perfil?.idiomas?.forEach((idioma, index) => {
      const hasLenguaje = idioma.lenguaje && idioma.lenguaje.trim()
      const hasNivel = idioma.nivel && idioma.nivel.trim()

      if ((hasLenguaje || hasNivel) && (!hasLenguaje || !hasNivel)) {
        errors.push(
          `El idioma ${index + 1} debe tener lenguaje y nivel completos`
        )
      }
    })

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Validate files before upload
   */
  validateUploadFiles(files: File[]): ValidationResult {
    const errors: string[] = []

    if (!files || files.length === 0) {
      errors.push('No files selected for upload')
      return { isValid: false, errors }
    }

    if (files.length > 10) {
      errors.push('Maximum 10 files can be uploaded at once')
    }

    files.forEach((file, index) => {
      // Check file type
      if (file.type !== 'application/pdf') {
        errors.push(
          `File ${index + 1} (${file.name}): Only PDF files are allowed`
        )
      }

      // Check file size (4.5MB limit)
      const maxSize = 4.5 * 1024 * 1024 // 4.5MB in bytes
      if (file.size > maxSize) {
        errors.push(
          `File ${index + 1} (${file.name}): File size exceeds 4.5MB limit`
        )
      }

      if (file.size === 0) {
        errors.push(`File ${index + 1} (${file.name}): File is empty`)
      }

      // Check filename
      if (!file.name || file.name.trim() === '') {
        errors.push(`File ${index + 1}: Filename is required`)
      }
    })

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Validate user data for creation
   */
  validateUserData(userData: CreateUserRequest): ValidationResult {
    const errors: string[] = []

    // Validate required fields
    if (!userData.email || !userData.email.trim()) {
      errors.push('Email is required')
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(userData.email)) {
        errors.push('Invalid email format')
      }
    }

    if (!userData.name || !userData.name.trim()) {
      errors.push('Name is required')
    }

    if (!userData.role || !userData.role.trim()) {
      errors.push('Role is required')
    }

    // Validate temporary password if provided
    if (userData.temporary_password && userData.temporary_password.length < 8) {
      errors.push('Temporary password must be at least 8 characters long')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Validate user update data
   */
  validateUserUpdateData(userData: UpdateUserRequest): ValidationResult {
    const errors: string[] = []

    // Name validation (if provided)
    if (
      userData.name !== undefined &&
      (!userData.name || !userData.name.trim())
    ) {
      errors.push('Name cannot be empty')
    }

    // Role validation (if provided)
    if (
      userData.role !== undefined &&
      (!userData.role || !userData.role.trim())
    ) {
      errors.push('Role cannot be empty')
    }

    // Status validation (if provided)
    if (userData.status !== undefined) {
      const validStatuses = ['active', 'inactive']
      if (!validStatuses.includes(userData.status)) {
        errors.push('Invalid status. Must be active or inactive')
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Validate role data for creation
   */
  validateRoleData(roleData: CreateRoleRequest): ValidationResult {
    const errors: string[] = []

    // Validate required fields
    if (!roleData.name || !roleData.name.trim()) {
      errors.push('Role name is required')
    } else {
      // Check for valid role name format (no special characters except spaces and hyphens)
      const nameRegex = /^[a-zA-Z0-9\s\-_]+$/
      if (!nameRegex.test(roleData.name)) {
        errors.push(
          'Role name can only contain letters, numbers, spaces, hyphens, and underscores'
        )
      }
    }

    if (!roleData.description || !roleData.description.trim()) {
      errors.push('Role description is required')
    }

    if (!roleData.permissions || !Array.isArray(roleData.permissions)) {
      errors.push('Permissions array is required')
    } else if (roleData.permissions.length === 0) {
      errors.push('At least one permission is required')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Validate role update data
   */
  validateRoleUpdateData(roleData: UpdateRoleRequest): ValidationResult {
    const errors: string[] = []

    // Description validation (if provided)
    if (
      roleData.description !== undefined &&
      (!roleData.description || !roleData.description.trim())
    ) {
      errors.push('Description cannot be empty')
    }

    // Permissions validation (if provided)
    if (roleData.permissions !== undefined) {
      if (!Array.isArray(roleData.permissions)) {
        errors.push('Permissions must be an array')
      } else if (roleData.permissions.length === 0) {
        errors.push('At least one permission is required')
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Validate workflow data for creation
   */
  validateWorkflowData(
    workflowData: CreateWorkflowRequest
  ): WorkflowValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Validate required fields
    if (!workflowData.name || !workflowData.name.trim()) {
      errors.push('Workflow name is required')
    } else {
      // Check for valid workflow name format
      const nameRegex = /^[a-zA-Z0-9\s\-_]+$/
      if (!nameRegex.test(workflowData.name)) {
        errors.push(
          'Workflow name can only contain letters, numbers, spaces, hyphens, and underscores'
        )
      }
    }

    if (!workflowData.workflow_logic) {
      errors.push('Workflow logic is required')
    } else {
      // Validate workflow logic structure
      const logicValidation = this.validateWorkflowLogic(
        workflowData.workflow_logic
      )
      errors.push(...logicValidation.errors)
      warnings.push(...(logicValidation.warnings || []))
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * Validate workflow update data
   */
  validateWorkflowUpdateData(
    workflowData: UpdateWorkflowRequest
  ): WorkflowValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Name validation (if provided)
    if (
      workflowData.name !== undefined &&
      (!workflowData.name || !workflowData.name.trim())
    ) {
      errors.push('Workflow name cannot be empty')
    }

    // Description validation (if provided)
    if (
      workflowData.description !== undefined &&
      workflowData.description !== null &&
      workflowData.description.length > 500
    ) {
      warnings.push('Description is quite long, consider shortening it')
    }

    // Workflow logic validation (if provided)
    if (workflowData.workflow_logic !== undefined) {
      const logicValidation = this.validateWorkflowLogic(
        workflowData.workflow_logic
      )
      errors.push(...logicValidation.errors)
      warnings.push(...(logicValidation.warnings || []))
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * Validate workflow logic structure recursively
   */
  private validateWorkflowLogic(
    logic: any,
    depth = 0
  ): WorkflowValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Check maximum nesting depth
    if (depth > 5) {
      errors.push('Workflow logic is too deeply nested (maximum 5 levels)')
      return { isValid: false, errors, warnings }
    }

    // Validate logic type
    if (!logic.type || !['AND', 'OR', 'USER', 'ROLE'].includes(logic.type)) {
      errors.push('Invalid logic type. Must be AND, OR, USER, or ROLE')
      return { isValid: false, errors, warnings }
    }

    // Validate based on type
    switch (logic.type) {
      case 'USER':
        if (!logic.user_id || !logic.user_id.trim()) {
          errors.push('User ID is required for USER type logic')
        }
        break

      case 'ROLE':
        if (!logic.role_name || !logic.role_name.trim()) {
          errors.push('Role name is required for ROLE type logic')
        }
        break

      case 'AND':
      case 'OR':
        if (!logic.conditions || !Array.isArray(logic.conditions)) {
          errors.push(
            `Conditions array is required for ${logic.type} type logic`
          )
        } else if (logic.conditions.length < 2) {
          errors.push(`${logic.type} logic must have at least 2 conditions`)
        } else if (logic.conditions.length > 10) {
          warnings.push(
            `${logic.type} logic has many conditions (${logic.conditions.length}), consider simplifying`
          )
        } else {
          // Recursively validate nested conditions
          logic.conditions.forEach((condition: any, index: number) => {
            const nestedValidation = this.validateWorkflowLogic(
              condition,
              depth + 1
            )
            nestedValidation.errors.forEach(error => {
              errors.push(`Condition ${index + 1}: ${error}`)
            })
            nestedValidation.warnings?.forEach(warning => {
              warnings.push(`Condition ${index + 1}: ${warning}`)
            })
          })
        }
        break
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * Handle user management specific errors
   */
  private handleUserManagementError(
    error: ApiError,
    defaultMessage: string
  ): Error {
    // Map specific error codes to user-friendly messages
    const errorMessages: Record<string, string> = {
      [UserManagementError.USER_NOT_FOUND]: 'Usuario no encontrado',
      [UserManagementError.EMAIL_ALREADY_EXISTS]: 'El email ya está registrado',
      [UserManagementError.INVALID_ROLE]: 'Rol inválido seleccionado',
      [UserManagementError.CANNOT_DELETE_SELF]:
        'No puedes eliminar tu propia cuenta',
      [UserManagementError.CANNOT_DELETE_LAST_ADMIN]:
        'No se puede eliminar el último administrador',
      [UserManagementError.ROLE_NOT_FOUND]: 'Rol no encontrado',
      [UserManagementError.CANNOT_DELETE_SYSTEM_ROLE]:
        'No se puede eliminar un rol del sistema',
      [UserManagementError.ROLE_HAS_USERS]:
        'No se puede eliminar un rol que tiene usuarios asignados',
      [UserManagementError.INSUFFICIENT_PERMISSIONS]:
        'Permisos insuficientes para esta acción',
    }

    // Try to extract error code from response
    let errorCode: string | undefined
    if (error.details && typeof error.details === 'object') {
      const details = error.details as any
      errorCode = details.error_code || details.code
    }

    // Use mapped message if available, otherwise use the original error message or default
    const message =
      errorCode && errorMessages[errorCode]
        ? errorMessages[errorCode]
        : error.message || defaultMessage

    const enhancedError = new Error(message) as Error & ApiError
    enhancedError.status = error.status
    enhancedError.details = error.details

    return enhancedError
  }

  /**
   * Check if user has permission for a specific action (client-side validation)
   * Note: This is for UI purposes only. Server-side validation is still required.
   */
  async checkUserPermission(
    resource: PermissionResource,
    action: PermissionAction
  ): Promise<boolean> {
    try {
      // This would typically check against the current user's permissions
      // For now, we'll return true and let the server handle the actual validation
      // In a full implementation, this would check the user's cached permissions
      return true
    } catch (error) {
      console.warn('Failed to check user permission:', error)
      return false
    }
  }

  // Utility Methods

  /**
   * Build redirect URL for candidates page after solicitud creation
   */
  buildCandidatesUrl(solicitudId: string): string {
    return `/candidates/${solicitudId}`
  }

  /**
   * Check if solicitud is ready for CV collection
   */
  isSolicitudActive(solicitud: unknown): boolean {
    return (solicitud as any)?.estado === 'active'
  }

  /**
   * Get upload progress summary
   */
  getUploadSummary(progress: UploadProgress[]): {
    total: number
    pending: number
    uploading: number
    completed: number
    error: number
    overallProgress: number
  } {
    const total = progress.length
    const pending = progress.filter(p => p.status === 'pending').length
    const uploading = progress.filter(p => p.status === 'uploading').length
    const completed = progress.filter(p => p.status === 'completed').length
    const error = progress.filter(p => p.status === 'error').length

    const overallProgress =
      total > 0
        ? Math.round(progress.reduce((sum, p) => sum + p.progress, 0) / total)
        : 0

    return {
      total,
      pending,
      uploading,
      completed,
      error,
      overallProgress,
    }
  }

  // User Management Utility Methods

  /**
   * Check if a user can be deleted (not self, not last admin)
   */
  canDeleteUser(
    userId: string,
    currentUserId: string,
    userRole: string,
    totalAdmins: number
  ): boolean {
    // Cannot delete self
    if (userId === currentUserId) {
      return false
    }

    // Cannot delete last administrator
    if (userRole === 'Administrador' && totalAdmins <= 1) {
      return false
    }

    return true
  }

  /**
   * Check if a role can be deleted (not system role, no users assigned)
   */
  canDeleteRole(role: RoleInfo): boolean {
    // Cannot delete system roles
    if (role.is_system_role) {
      return false
    }

    // Cannot delete roles with users
    if (role.user_count > 0) {
      return false
    }

    return true
  }

  /**
   * Format user display name
   */
  formatUserDisplayName(user: UserInfo): string {
    return user.name || user.email
  }

  /**
   * Get user status display text
   */
  getUserStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      active: 'Activo',
      inactive: 'Inactivo',
      pending_confirmation: 'Pendiente de confirmación',
    }
    return statusMap[status] || status
  }

  /**
   * Get role display name with user count
   */
  getRoleDisplayName(role: RoleInfo): string {
    return `${role.name} (${role.user_count} usuarios)`
  }

  // ===== JOB DEFINITION MANAGEMENT =====

  /**
   * Get list of job definitions with pagination and filtering
   */
  async getJobDefinitions(params?: {
    size?: number
    start?: string
    search?: string
    active_only?: boolean
  }): Promise<JobDefinitionListResponse> {
    try {
      const queryParams = new URLSearchParams()

      if (params?.size) queryParams.append('size', params.size.toString())
      if (params?.start) queryParams.append('start', params.start)
      if (params?.search) queryParams.append('search', params.search)
      if (params?.active_only !== undefined) {
        queryParams.append('active_only', params.active_only.toString())
      }

      const url = queryParams.toString()
        ? `${this.jobDefinitionsURL}?${queryParams.toString()}`
        : this.jobDefinitionsURL

      return await this.executeWithRetry<JobDefinitionListResponse>({
        method: 'GET',
        url,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch job definitions'
      )
    }
  }

  /**
   * Get a specific job definition by ID
   */
  async getJobDefinition(id: string): Promise<JobDefinitionResponse> {
    try {
      if (!id) {
        throw new Error('Job definition ID is required')
      }

      return await this.executeWithRetry<JobDefinitionResponse>({
        method: 'GET',
        url: `${this.jobDefinitionsURL}/${id}`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch job definition'
      )
    }
  }

  /**
   * Create a new job definition
   */
  async createJobDefinition(
    jobDefinitionData: CreateJobDefinitionRequest
  ): Promise<JobDefinitionResponse> {
    try {
      // Validate job definition data
      const validation = this.validateJobDefinitionData(jobDefinitionData)
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      const response = await this.executeWithRetry<ApiResponse<JobDefinition>>({
        method: 'POST',
        url: this.jobDefinitionsURL,
        data: jobDefinitionData,
      })
      return { data: response.data }
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to create job definition'
      )
    }
  }

  /**
   * Update an existing job definition
   */
  async updateJobDefinition(
    id: string,
    jobDefinitionData: UpdateJobDefinitionRequest
  ): Promise<JobDefinitionResponse> {
    try {
      if (!id) {
        throw new Error('Job definition ID is required')
      }

      // Validate job definition data
      const validation = this.validateJobDefinitionData(jobDefinitionData)
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      const response = await this.executeWithRetry<ApiResponse<JobDefinition>>({
        method: 'PUT',
        url: `${this.jobDefinitionsURL}/${id}`,
        data: jobDefinitionData,
      })
      return { data: response.data }
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to update job definition'
      )
    }
  }

  /**
   * Delete a job definition (soft delete)
   */
  async deleteJobDefinition(id: string): Promise<void> {
    try {
      if (!id) {
        throw new Error('Job definition ID is required')
      }

      await this.executeWithRetry<void>({
        method: 'DELETE',
        url: `${this.jobDefinitionsURL}/${id}`,
      })
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to delete job definition'
      )
    }
  }

  /**
   * Get job definitions formatted for dropdown selection
   */
  async getJobDefinitionOptions(): Promise<JobDefinitionSelectorOption[]> {
    try {
      const response = await this.getJobDefinitions({ active_only: true })
      return response.data.items.map(jd => ({
        value: jd.id,
        label: jd.name,
        puesto: jd.puesto,
      }))
    } catch (error) {
      const apiError = error as ApiError
      throw this.handleUserManagementError(
        apiError,
        'Failed to fetch job definition options'
      )
    }
  }

  /**
   * Validate job definition data
   */
  validateJobDefinitionData(
    jobDefinitionData: CreateJobDefinitionRequest | UpdateJobDefinitionRequest
  ): ValidationResult {
    const errors: string[] = []

    // Validate required fields
    if (!jobDefinitionData.name || !jobDefinitionData.name.trim()) {
      errors.push('Job definition name is required')
    } else if (jobDefinitionData.name.length < 3) {
      errors.push('Job definition name must be at least 3 characters')
    } else if (jobDefinitionData.name.length > 100) {
      errors.push('Job definition name must be less than 100 characters')
    }

    if (!jobDefinitionData.puesto || !jobDefinitionData.puesto.trim()) {
      errors.push('Position title is required')
    } else if (jobDefinitionData.puesto.length < 3) {
      errors.push('Position title must be at least 3 characters')
    } else if (jobDefinitionData.puesto.length > 100) {
      errors.push('Position title must be less than 100 characters')
    }

    // Validate functions
    if (
      !jobDefinitionData.funciones ||
      jobDefinitionData.funciones.length === 0
    ) {
      errors.push('At least one function is required')
    } else {
      let totalOcupabilidad = 0
      jobDefinitionData.funciones.forEach((funcion, index) => {
        if (!funcion.funcion || !funcion.funcion.trim()) {
          errors.push(`Function ${index + 1} description is required`)
        } else if (funcion.funcion.length < 10) {
          errors.push(
            `Function ${index + 1} description must be at least 10 characters`
          )
        } else if (funcion.funcion.length > 500) {
          errors.push(
            `Function ${index + 1} description must be less than 500 characters`
          )
        }

        if (
          funcion.ocupabilidad === undefined ||
          funcion.ocupabilidad === null
        ) {
          errors.push(`Function ${index + 1} ocupabilidad is required`)
        } else if (funcion.ocupabilidad < 1 || funcion.ocupabilidad > 100) {
          errors.push(
            `Function ${index + 1} ocupabilidad must be between 1 and 100`
          )
        }

        totalOcupabilidad += funcion.ocupabilidad || 0
      })

      if (totalOcupabilidad !== 100) {
        errors.push(
          `Total ocupabilidad must equal 100%, currently ${totalOcupabilidad}%`
        )
      }
    }

    // Validate profile data (optional fields)
    if (jobDefinitionData.perfil) {
      // Validate languages
      if (jobDefinitionData.perfil.idiomas) {
        jobDefinitionData.perfil.idiomas.forEach((idioma, index) => {
          if (idioma.lenguaje && !idioma.nivel) {
            errors.push(
              `Language ${index + 1} level is required when language is specified`
            )
          }
          if (idioma.nivel && !idioma.lenguaje) {
            errors.push(
              `Language ${index + 1} name is required when level is specified`
            )
          }
        })
      }

      // Validate studies
      if (jobDefinitionData.perfil.estudios) {
        jobDefinitionData.perfil.estudios.forEach((estudio, index) => {
          if (estudio.nivel && !estudio.detalle) {
            errors.push(
              `Study ${index + 1} detail is required when level is specified`
            )
          }
          if (estudio.detalle && !estudio.nivel) {
            errors.push(
              `Study ${index + 1} level is required when detail is specified`
            )
          }
        })
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }
}

// Create singleton instance
export const apiService = new ApiService()
